/**
 * Release Date Filtering Utilities
 * 
 * Utilities for filtering releases by date, specifically for September 2025 onwards
 * and other date-based filtering requirements.
 */

import { ProjectVersion } from '../services/apiService'
import { CategorizedRelease } from '../types/release'

// ============================================================================
// DATE CONSTANTS
// ============================================================================

/** Default minimum date for release timeline (September 1, 2025) */
export const DEFAULT_MIN_DATE = new Date('2025-09-01')

/** Date format for display */
export const DISPLAY_DATE_FORMAT = 'MMM dd, yyyy'

/** Date format for API queries */
export const API_DATE_FORMAT = 'yyyy-MM-dd'

// ============================================================================
// DATE FILTERING FUNCTIONS
// ============================================================================

/**
 * Filter releases by minimum date (September 2025 onwards by default)
 */
export function filterReleasesByMinDate(
  releases: ProjectVersion[] | CategorizedRelease[],
  minDate: Date = DEFAULT_MIN_DATE
): typeof releases {
  return releases.filter(release => {
    // Include unreleased versions (they don't have a release date)
    if (!release.releaseDate) {
      return !release.released // Only include if not yet released
    }
    
    const releaseDate = new Date(release.releaseDate)
    
    // Skip invalid dates
    if (isNaN(releaseDate.getTime())) {
      return false
    }
    
    return releaseDate >= minDate
  })
}

/**
 * Filter releases by date range
 */
export function filterReleasesByDateRange(
  releases: ProjectVersion[] | CategorizedRelease[],
  startDate: Date,
  endDate?: Date
): typeof releases {
  return releases.filter(release => {
    // Handle unreleased versions
    if (!release.releaseDate) {
      // Include unreleased if no end date specified
      return !endDate && !release.released
    }
    
    const releaseDate = new Date(release.releaseDate)
    
    // Skip invalid dates
    if (isNaN(releaseDate.getTime())) {
      return false
    }
    
    // Check date range
    if (releaseDate < startDate) {
      return false
    }
    
    if (endDate && releaseDate > endDate) {
      return false
    }
    
    return true
  })
}

/**
 * Filter releases by release status
 */
export function filterReleasesByStatus(
  releases: ProjectVersion[] | CategorizedRelease[],
  includeReleased: boolean = true,
  includeUnreleased: boolean = true
): typeof releases {
  return releases.filter(release => {
    if (release.released && includeReleased) {
      return true
    }
    
    if (!release.released && includeUnreleased) {
      return true
    }
    
    return false
  })
}

/**
 * Get releases for specific time periods
 */
export function getReleasesByTimePeriod(
  releases: ProjectVersion[] | CategorizedRelease[],
  period: 'upcoming' | 'current-quarter' | 'current-year' | 'next-quarter' | 'next-year'
): typeof releases {
  const now = new Date()
  const currentYear = now.getFullYear()
  const currentQuarter = Math.floor(now.getMonth() / 3)
  
  let startDate: Date
  let endDate: Date | undefined
  
  switch (period) {
    case 'upcoming':
      startDate = now
      endDate = undefined // No end date for upcoming
      break
      
    case 'current-quarter':
      startDate = new Date(currentYear, currentQuarter * 3, 1)
      endDate = new Date(currentYear, (currentQuarter + 1) * 3, 0) // Last day of quarter
      break
      
    case 'current-year':
      startDate = new Date(currentYear, 0, 1)
      endDate = new Date(currentYear, 11, 31)
      break
      
    case 'next-quarter':
      const nextQuarter = (currentQuarter + 1) % 4
      const nextQuarterYear = nextQuarter === 0 ? currentYear + 1 : currentYear
      startDate = new Date(nextQuarterYear, nextQuarter * 3, 1)
      endDate = new Date(nextQuarterYear, (nextQuarter + 1) * 3, 0)
      break
      
    case 'next-year':
      startDate = new Date(currentYear + 1, 0, 1)
      endDate = new Date(currentYear + 1, 11, 31)
      break
      
    default:
      throw new Error(`Unknown time period: ${period}`)
  }
  
  return filterReleasesByDateRange(releases, startDate, endDate)
}

// ============================================================================
// DATE VALIDATION FUNCTIONS
// ============================================================================

/**
 * Validate release date
 */
export function validateReleaseDate(dateString: string): {
  isValid: boolean
  date?: Date
  error?: string
} {
  if (!dateString || typeof dateString !== 'string') {
    return {
      isValid: false,
      error: 'Date string is required'
    }
  }
  
  const date = new Date(dateString)
  
  if (isNaN(date.getTime())) {
    return {
      isValid: false,
      error: 'Invalid date format'
    }
  }
  
  // Check for reasonable date range (not too far in past or future)
  const now = new Date()
  const minYear = 2020
  const maxYear = now.getFullYear() + 10
  
  if (date.getFullYear() < minYear) {
    return {
      isValid: false,
      error: `Date is too far in the past (before ${minYear})`
    }
  }
  
  if (date.getFullYear() > maxYear) {
    return {
      isValid: false,
      error: `Date is too far in the future (after ${maxYear})`
    }
  }
  
  return {
    isValid: true,
    date
  }
}

/**
 * Check if release is upcoming (future release date)
 */
export function isUpcomingRelease(release: ProjectVersion | CategorizedRelease): boolean {
  if (release.released) {
    return false
  }
  
  if (!release.releaseDate) {
    return true // Unreleased with no date is considered upcoming
  }
  
  const releaseDate = new Date(release.releaseDate)
  if (isNaN(releaseDate.getTime())) {
    return false
  }
  
  return releaseDate > new Date()
}

/**
 * Check if release is overdue (past release date but not marked as released)
 */
export function isOverdueRelease(release: ProjectVersion | CategorizedRelease): boolean {
  if (release.released) {
    return false
  }
  
  if (!release.releaseDate) {
    return false // No date means we can't determine if overdue
  }
  
  const releaseDate = new Date(release.releaseDate)
  if (isNaN(releaseDate.getTime())) {
    return false
  }
  
  return releaseDate < new Date()
}

// ============================================================================
// DATE FORMATTING FUNCTIONS
// ============================================================================

/**
 * Format date for display
 */
export function formatDisplayDate(date: Date | string): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date
  
  if (isNaN(dateObj.getTime())) {
    return 'Invalid Date'
  }
  
  return dateObj.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

/**
 * Format date for API queries
 */
export function formatApiDate(date: Date): string {
  return date.toISOString().split('T')[0] // YYYY-MM-DD format
}

/**
 * Get relative date description
 */
export function getRelativeDateDescription(date: Date | string): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date
  
  if (isNaN(dateObj.getTime())) {
    return 'Invalid Date'
  }
  
  const now = new Date()
  const diffMs = dateObj.getTime() - now.getTime()
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24))
  
  if (diffDays === 0) {
    return 'Today'
  } else if (diffDays === 1) {
    return 'Tomorrow'
  } else if (diffDays === -1) {
    return 'Yesterday'
  } else if (diffDays > 0) {
    if (diffDays <= 7) {
      return `In ${diffDays} days`
    } else if (diffDays <= 30) {
      const weeks = Math.ceil(diffDays / 7)
      return `In ${weeks} week${weeks > 1 ? 's' : ''}`
    } else if (diffDays <= 365) {
      const months = Math.ceil(diffDays / 30)
      return `In ${months} month${months > 1 ? 's' : ''}`
    } else {
      const years = Math.ceil(diffDays / 365)
      return `In ${years} year${years > 1 ? 's' : ''}`
    }
  } else {
    const absDays = Math.abs(diffDays)
    if (absDays <= 7) {
      return `${absDays} days ago`
    } else if (absDays <= 30) {
      const weeks = Math.ceil(absDays / 7)
      return `${weeks} week${weeks > 1 ? 's' : ''} ago`
    } else if (absDays <= 365) {
      const months = Math.ceil(absDays / 30)
      return `${months} month${months > 1 ? 's' : ''} ago`
    } else {
      const years = Math.ceil(absDays / 365)
      return `${years} year${years > 1 ? 's' : ''} ago`
    }
  }
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Get date range for timeline display
 */
export function getTimelineeDateRange(
  releases: (ProjectVersion | CategorizedRelease)[],
  minDate: Date = DEFAULT_MIN_DATE
): { start: Date; end: Date } {
  const validDates = releases
    .filter(release => release.releaseDate)
    .map(release => new Date(release.releaseDate!))
    .filter(date => !isNaN(date.getTime()) && date >= minDate)

  if (validDates.length === 0) {
    // No valid dates, return default range
    const end = new Date(minDate)
    end.setFullYear(end.getFullYear() + 2) // 2 years from min date
    return { start: minDate, end }
  }

  const earliestDate = new Date(Math.min(...validDates.map(d => d.getTime())))
  const latestDate = new Date(Math.max(...validDates.map(d => d.getTime())))

  // Ensure we don't go before the minimum date
  const start = earliestDate < minDate ? minDate : earliestDate

  // Add some padding to the end date
  const end = new Date(latestDate)
  end.setMonth(end.getMonth() + 3) // 3 months padding

  return { start, end }
}

/**
 * Calculate extended timeline range for scrollable timeline
 * Ensures all releases are visible and extends to at least 6 months from today
 */
export function calculateExtendedTimelineRange(
  releases: (ProjectVersion | CategorizedRelease)[],
  startDate: Date = new Date('2025-09-01')
): { start: Date; end: Date } {
  const validDates = releases
    .filter(release => release.releaseDate)
    .map(release => new Date(release.releaseDate!))
    .filter(date => !isNaN(date.getTime()))

  // Start date is fixed to September 1st
  const start = new Date(startDate)

  // Calculate end date: whichever is later between last release date or today + 6 months
  const today = new Date()
  const sixMonthsFromToday = new Date(today)
  sixMonthsFromToday.setMonth(sixMonthsFromToday.getMonth() + 6)

  let end: Date

  if (validDates.length === 0) {
    // No valid release dates, use 6 months from today
    end = sixMonthsFromToday
  } else {
    // Find the latest release date
    const latestReleaseDate = new Date(Math.max(...validDates.map(d => d.getTime())))

    // Use whichever is later: latest release date or 6 months from today
    end = latestReleaseDate > sixMonthsFromToday ? latestReleaseDate : sixMonthsFromToday
  }

  // Add a small buffer to the end date (1 month)
  end.setMonth(end.getMonth() + 1)

  return { start, end }
}
